import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import { Users, DollarSign, TrendingUp, LogOut, Dumbbell } from 'lucide-react';
import Relatorios from './Relatorios';

const API_URL = "https://proprietario.mels.com.br/api";

const COLORS = ['#22c55e', '#3b82f6', '#f97316', '#a855f7', '#ef4444', '#06b6d4'];

const formatarDataBR = (data) => {
  if (!data) return '';
  try {
    if (typeof data === 'number') {
      const d = new Date(data);
      return d.toLocaleDateString('pt-BR');
    }

    if (typeof data === 'string' && data.includes('T')) {
      const d = new Date(data);
      return d.toLocaleDateString('pt-BR');
    }

    if (typeof data === 'string' && data.includes('-')) {
      const [ano, mes, dia] = data.split('-');
      if (ano && mes && dia) {
        return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`;
      }
    }

    if (typeof data === 'string' && data.includes('/')) {
      return data;
    }

    const d = new Date(data + 'T00:00:00');
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('pt-BR');
    }

    return '';
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

const traduzirMes = (mesIngles) => {
  const meses = {
    'Jan': 'Jan', 
    'Feb': 'Fev', 
    'Mar': 'Mar', 
    'Apr': 'Abr',
    'May': 'Mai', 
    'Jun': 'Jun', 
    'Jul': 'Jul', 
    'Aug': 'Ago',
    'Sep': 'Set', 
    'Oct': 'Out', 
    'Nov': 'Nov', 
    'Dec': 'Dez'
  };
  return meses[mesIngles] || mesIngles;
};

export default function GymOwnerDashboard() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedAcademia, setSelectedAcademia] = useState(null);
  const [cpf, setCpf] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [periodoSelecionado, setPeriodoSelecionado] = useState('mes');
  const [mostrarFiltroPersonalizado, setMostrarFiltroPersonalizado] = useState(false);
  const [datainicio, setDataInicio] = useState('');
  const [datafim, setDataFim] = useState('');
  const [visualizacao, setVisualizacao] = useState('dashboard');
  const isLoadingRef = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');
    const academiaData = localStorage.getItem('academia');

    if (token && userData) {
      setIsLoggedIn(true);
      const user = JSON.parse(userData);
      setCurrentUser(user);

      if (academiaData) {
        const academia = JSON.parse(academiaData);

        if (academia.id === 'todas' && user.academias) {
          academia.academias = user.academias.map(a => ({ id: a.id, nome: a.nome }));
        }
        setSelectedAcademia(academia);
      }
    }
  }, []);

  const calcularDatas = (periodo) => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = hoje.getMonth();
    const dia = hoje.getDate();

    let inicio, fim;

    switch (periodo) {
      case 'hoje':
        inicio = fim = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
        break;

      case 'semana':
        const seteDiasAtras = new Date(hoje);
        seteDiasAtras.setDate(dia - 7);
        inicio = `${seteDiasAtras.getFullYear()}-${String(seteDiasAtras.getMonth() + 1).padStart(2, '0')}-${String(seteDiasAtras.getDate()).padStart(2, '0')}`;
        fim = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
        break;

      case 'mes':
        inicio = `${ano}-${String(mes + 1).padStart(2, '0')}-01`;
        fim = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
        break;

      case 'ano':
        inicio = `${ano}-01-01`;
        fim = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
        break;

      case 'personalizado':
        return { inicio: datainicio, fim: datafim };

      default:
        return null;
    }

    return { inicio, fim };
  };

  const buscarDadosFiltrados = useCallback(async () => {
    if (!selectedAcademia?.id || isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      setLoading(true);

      let inicio, fim;

      if (periodoSelecionado === 'personalizado') {
        if (!datainicio || !datafim) {
          alert('Por favor, selecione as datas de início e fim');
          isLoadingRef.current = false;
          setLoading(false);
          return;
        }
        inicio = datainicio;
        fim = datafim;
      } else {
        const datas = calcularDatas(periodoSelecionado);
        inicio = datas.inicio;
        fim = datas.fim;
      }

      let url;

      if (selectedAcademia.id === 'todas') {
        console.log('=== DEBUG TODAS AS ACADEMIAS ===');
        console.log('selectedAcademia:', selectedAcademia);
        console.log('currentUser:', currentUser);

        const academias = selectedAcademia.academias || currentUser?.academias || [];

        if (academias.length === 0) {
          console.error(' ERRO: Nenhuma academia encontrada!');
          alert('Erro: Nenhuma academia encontrada para consolidar');
          isLoadingRef.current = false;
          setLoading(false);
          return;
        }

        const ids = academias.map(a => a.id).join(',');

        console.log(' IDs extraídos:', ids);
        console.log('Total de academias:', academias.length);

        url = `${API_URL}/academias/dashboard-consolidado?ids=${ids}&datainicio=${inicio}&datafim=${fim}`;
      } else {
        url = `${API_URL}/academia/${selectedAcademia.id}/dashboard-filtrado?datainicio=${inicio}&datafim=${fim}`;
      }

      console.log('Fazendo requisição para:', url);

      const response = await fetch(url);

      if (!response.ok) throw new Error('Erro ao buscar dados filtrados');

      const data = await response.json();

      setSelectedAcademia(prev => ({
        ...prev,
        totalMembros: data.totalMembros,
        receitaMensal: data.receitaMensal,
        receitaDiaria: data.receitaDiaria,
        crescimento: data.crescimento,
        receitasPorMes: data.receitasPorMes || [],
        receitasPorFormaPgto: data.receitasPorFormaPgto || [],
        planosAtivos: data.planosAtivos || [],
        pagamentosRecentes: data.pagamentosRecentes || [],
        clientesNovos: data.clientesNovos || [],
        clientesExcluidos: data.clientesExcluidos || [],
        clientesNovosPorMes: data.clientesNovosPorMes || [],
        periodoFiltrado: {
          datainicio: inicio,
          datafim: fim
        }
      }));

    } catch (error) {
      console.error(' Erro ao buscar dados filtrados:', error);
      alert('Erro ao buscar dados filtrados: ' + error.message);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [periodoSelecionado, selectedAcademia?.id, datainicio, datafim, currentUser]);

  useEffect(() => {
    if (selectedAcademia?.id && periodoSelecionado && periodoSelecionado !== 'personalizado') {
      const timer = setTimeout(() => {
        buscarDadosFiltrados();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [periodoSelecionado, selectedAcademia?.id]);

  const handlePeriodoChange = (novoPeriodo) => {
    setPeriodoSelecionado(novoPeriodo);
    setMostrarFiltroPersonalizado(novoPeriodo === 'personalizado');
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const cpfLimpo = cpf.replace(/\D/g, '');

      if (!cpfLimpo || cpfLimpo.length !== 11) {
        throw new Error('CPF inválido');
      }

      if (!senha) {
        throw new Error('Senha não pode estar vazia');
      }

      const userData = await apiLogin(cpfLimpo, senha);

      localStorage.setItem('token', userData.token || 'logged-in');
      localStorage.setItem('userData', JSON.stringify(userData));

      setCurrentUser(userData);

      if (userData.academias && userData.academias.length > 0) {
        const academiaBasica = {
          id: userData.academias[0].id,
          nome: userData.academias[0].nome
        };
        setSelectedAcademia(academiaBasica);
        localStorage.setItem('academia', JSON.stringify(academiaBasica));
      } else {
        setSelectedAcademia(null);
      }

      setIsLoggedIn(true);

    } catch (err) {
      console.error('Erro no login:', err);
      setError(err.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('academia');

    setIsLoggedIn(false);
    setCurrentUser(null);
    setSelectedAcademia(null);
    setCpf('');
    setSenha('');
    setError('');
    setVisualizacao('dashboard');
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return numbers.slice(0, 11);
  };

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 border-2 border-green-100">
          <img
            src="/ic.png"
            alt="Mel Fitness"
            style={{
              width: '300px',
              marginBottom: '40px',
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}
          />
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2 mt-6">Acesse sua conta</h2>
          <p className="text-center text-gray-600 mb-8">Entre com seu CPF e senha</p>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CPF</label>
              <input
                type="text"
                value={cpf}
                onChange={handleCPFChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="000.000.000-00"
                maxLength={14}
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Senha</label>
              <input
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="••••••"
                disabled={loading}
              />
            </div>
            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Entrando' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!currentUser.academias || currentUser.academias.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <span className="text-2xl font-bold text-green-600">Mel Fitness</span>
              </div>
              <button onClick={handleLogOut} className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Dumbbell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Nenhuma academia vinculada</h2>
            <p className="text-gray-600">Entre em contato com o administrador para vincular suas academias.</p>
          </div>
        </main>
      </div>
    );
  }

  const academia = selectedAcademia;

  return (
    <div className="dashboard-wrapper min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-3 py-3 md:max-w-7xl md:mx-auto md:px-6 md:py-4">
          {/* Container principal - Coluna no mobile, linha no desktop */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            
            {/* SEÇÃO SUPERIOR - Logo + Info Academia */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
              {/* Logo */}
              <div className="flex flex-col items-center md:items-start text-center md:text-left">
                <span className="text-xl md:text-2xl font-bold text-green-600" style={{ fontFamily: 'Arial, sans-serif' }}>
                  Mel Fitness
                </span>
                <span className="text-xs text-green-500 uppercase tracking-wider">
                  Software para Academia
                </span>
              </div>
              
              {/* Separador - apenas desktop */}
              <div className="hidden md:block border-l-2 border-gray-300 h-12"></div>
              
              {/* Info da Academia */}
              <div className="text-center md:text-left">
                <h1 className="text-lg md:text-xl font-bold text-gray-900">{academia.nome}</h1>
                <p className="text-xs md:text-sm text-gray-600">Proprietário: {currentUser.nome}</p>
              </div>
            </div>

            {/* SEÇÃO INFERIOR - Botões e Controles */}
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
              
              {/* Container dos botões Dashboard/Relatórios */}
              <div className="flex flex-col md:flex-row gap-2">
                <button
                  onClick={() => setVisualizacao('dashboard')}
                  className={`w-full md:w-auto px-4 py-2 rounded-lg font-medium transition duration-200 ${
                    visualizacao === 'dashboard'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setVisualizacao('relatorios')}
                  className={`w-full md:w-auto px-4 py-2 rounded-lg font-medium transition duration-200 ${
                    visualizacao === 'relatorios'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Relatórios
                </button>
              </div>

              {/* Select de Academia */}
              {currentUser.academias.length > 1 && (
                <select
                  value={academia.id || 'todas'}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'todas') {
                      const todasAcademias = {
                        id: 'todas',
                        nome: 'Todas as Academias',
                        academias: currentUser.academias.map(a => ({ id: a.id, nome: a.nome }))
                      };
                      setSelectedAcademia(todasAcademias);
                      localStorage.setItem('academia', JSON.stringify(todasAcademias));
                    } else {
                      const academiaEncontrada = currentUser.academias.find(a => a.id === parseInt(value));
                      const academiaBasica = {
                        id: academiaEncontrada.id,
                        nome: academiaEncontrada.nome
                      };
                      setSelectedAcademia(academiaBasica);
                      localStorage.setItem('academia', JSON.stringify(academiaBasica));
                    }
                  }}
                  className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  <option value="todas"> Todas as Academias</option>
                  {currentUser.academias.map(a => (
                    <option key={a.id} value={a.id}>{a.nome}</option>
                  ))}
                </select>
              )}
              
              {/* Botão Sair */}
              <button
                onClick={handleLogOut}
                className="w-full md:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {visualizacao === 'dashboard' ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 mb-8 rounded-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">Período:</label>
                <select
                  value={periodoSelecionado}
                  onChange={(e) => handlePeriodoChange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="hoje">Hoje</option>
                  <option value="semana">Última Semana</option>
                  <option value="mes">Último Mês</option>
                  <option value="ano">Ano atual</option>
                  <option value="personalizado">Período Personalizado</option>
                </select>
              </div>

              {mostrarFiltroPersonalizado && (
                <div className="filtro-personalizado-container">
                  <div className="filtro-data-grupo">
                    <label className="text-sm text-gray-600">De:</label>
                    <input
                      type="date"
                      value={datainicio}
                      onChange={(e) => setDataInicio(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div className="filtro-data-grupo">
                    <label className="text-sm text-gray-600">Até:</label>
                    <input
                      type="date"
                      value={datafim}
                      onChange={(e) => setDataFim(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={buscarDadosFiltrados}
                    className="filtro-botao px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 font-medium"
                  >
                    Filtrar
                  </button>
                </div>
              )}

              {!mostrarFiltroPersonalizado && (
                <div className="text-sm text-gray-600">
                  {periodoSelecionado === 'hoje' && 'Mostrando dados de hoje'}
                  {periodoSelecionado === 'semana' && 'Últimos 7 dias'}
                  {periodoSelecionado === 'mes' && 'Último mês'}
                  {periodoSelecionado === 'ano' && 'Ano atual'}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Clientes Ativos</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{academia.totalMembros || 0}</p>
                </div>
                <Users className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Receita</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    R$ {(academia.receitaMensal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Evolução de Receita</h2>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={academia.receitasPorMes || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tickFormatter={(value) => traduzirMes(value)} />
                  <YAxis />
                  <Tooltip 
                  formatter={(value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                   labelFormatter={(label) => traduzirMes(label)}
                    />
                  <Line type="monotone" dataKey="receita" stroke="#16a34a" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Receita por Forma de Pagamento</h2>
              {(academia.receitasPorFormaPgto || []).length === 0 ? (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Nenhum dado disponível
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={academia.receitasPorFormaPgto.filter(item => item.valor > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={({ percent }) => {
                          if (percent > 0.02) {
                            return `${(percent * 100).toFixed(0)}%`;
                          }
                          return '';
                        }}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="valor"
                        paddingAngle={2}
                        minAngle={5}
                      >
                        {academia.receitasPorFormaPgto.filter(item => item.valor > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '8px', padding: '10px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                    {(academia.receitasPorFormaPgto || [])
                      .filter(item => item.valor > 0)
                      .sort((a, b) => b.valor - a.valor)
                      .map((item, index) => {
                        const total = academia.receitasPorFormaPgto.reduce((sum, i) => sum + (i.valor || 0), 0);
                        const percentual = ((item.valor / total) * 100).toFixed(1);

                        return (
                          <div key={index} className="flex items-center justify-between py-1">
                            <div className="flex items-center space-x-2 flex-1">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              ></div>
                              <span className="text-sm text-gray-700 truncate">{item.nome}</span>
                              <span className="text-xs text-gray-500">({percentual}%)</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 ml-2">
                              R$ {item.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Clientes Novos (Últimos 6 Meses)</h2>
              {(academia.clientesNovosPorMes || []).length === 0 ? (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Nenhum dado disponível
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={academia.clientesNovosPorMes || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="mes"
                      tickFormatter={(value) => traduzirMes(value)}
                    />
                  <YAxis allowDecimals={false} />
                 <Tooltip 
                    formatter={(value) => [`${value} clientes`, 'Novos']}
                     labelFormatter={(label) => traduzirMes(label)}
                  />
                    <Bar
                      dataKey="quantidade"
                      fill="#22c55e"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Planos Ativos</h2>
            <div className="space-y-3">
              {(academia.planosAtivos || []).map((plano, index) => (
                <div key={index} className="border-l-4 pl-4 py-2" style={{ borderColor: COLORS[index % COLORS.length] }}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm">{plano.plano}</p>
                      <p className="text-xs text-gray-600 mt-1">{plano.clientes} clientes ativos</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">R$ {plano.receita.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Pagamentos Recentes</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Cliente</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Valor</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Forma Pgto</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Data</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {(academia.pagamentosRecentes || []).map((pagamento) => (
                    <tr key={pagamento.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900">{pagamento.nome}</td>
                      <td className="py-3 px-4 text-sm font-semibold text-green-600">
                        R$ {pagamento.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">{pagamento.forma_pgto}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {formatarDataBR(pagamento.data)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${pagamento.tipo === 'CLIENTE NOVO'
                          ? 'bg-blue-100 text-blue-800'
                          : pagamento.tipo === 'RENOVAÇÃO'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {pagamento.tipo}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Clientes Novos</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Nome</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Atividade</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Data</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedAcademia.clientesNovos || []).map((cliente) => (
                    <tr key={cliente.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900">{cliente.nome}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{cliente.atividade}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {formatarDataBR(cliente.data)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">{cliente.hora}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Clientes Excluídos</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Nome</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Funcionário</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Data</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedAcademia.clientesExcluidos || []).map((cliente) => (
                    <tr key={cliente.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900">{cliente.nome}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{cliente.funcionario}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {formatarDataBR(cliente.data)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">{cliente.hora}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      ) : (
        <Relatorios academia={selectedAcademia} currentUser={currentUser} />
      )}
    </div>
  );
}

const apiLogin = async (cpfLimpo, senha) => {
  const response = await fetch(`${API_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cpf: cpfLimpo, senha })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Erro ao fazer login');
  }

  return data;
};